import {
  FormFieldJson,
  FormInputJson,
  FormulaJson,
  TableCellData,
  TableColumnJson,
  getTableCellPrimitiveValue,
} from '@doss/common-all'
import { Typography } from '@mui/material'
import { isNil } from 'lodash'
import { Children } from 'react'
import { tss } from 'tss-react/mui'
import { FormulaExpression } from '~/components/formula/FormulaExpression'
import { ConditionExpressionColumnChip } from './ConditionExpressionColumnChip'

const useStyles = tss.withName('ConditionExpressionValue').create(() => ({
  container: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
    whiteSpace: 'nowrap',
  },
}))

interface ConditionExpressionValueProps {
  value: FormulaJson | TableCellData | TableColumnJson | FormFieldJson | FormInputJson
}

/**
 * Given a left or right comparison value of a condition expression, returns the user visible value
 */
export function ConditionExpressionValue({ value }: ConditionExpressionValueProps) {
  const { classes } = useStyles()

  if ('operator' in value) {
    return <FormulaExpression data={value} />
  } else if ('id' in value) {
    // Handle TableColumnJson, FormFieldJson (both have 'name'), or FormInputJson (has 'label')
    const displayName = 'label' in value ? value.label : value.name
    return (
      <div className={classes.container}>
        <ConditionExpressionColumnChip name={displayName} value={value} />
      </div>
    )
  } else {
    const primitiveValue = getTableCellPrimitiveValue(value)

    if (Array.isArray(primitiveValue)) {
      return (
        <div className={classes.container}>
          <div>{`[`}</div>
          {Children.toArray(
            primitiveValue.map((val, idx) => (
              <>
                <Typography noWrap>{val}</Typography>
                {idx < primitiveValue.length - 1 && <div>,</div>}
              </>
            )),
          )}
          <div>{`]`}</div>
        </div>
      )
    }
    return <Typography noWrap>{!isNil(primitiveValue) ? primitiveValue.toString() : ''}</Typography>
  }
}
